<script setup>
import { computed, inject, toValue } from 'vue';
import { useField, FormContextKey } from 'vee-validate';

const props = defineProps({
    // Prefixo dos ids das opções. Sem ele, usa o `name`.
    id: {
        type: String,
        default: undefined
    },
    // Classes da coluna em volta do campo.
    classeColuna: {
        type: String,
        default: 'col-12'
    },
    // Nome do campo no schema do useForm. Quando informado, o componente se conecta
    // sozinho ao formulário pai (valor e erro), dispensando v-model e :erro.
    name: {
        type: String,
        default: null
    },
    erro: {
        type: String,
        default: null
    },
    modelValue: {
        type: [String, Number, Boolean],
        default: null
    },
    // Lista de { valor, rotulo, descricao?, icone? } (icone: classe do PrimeIcons).
    opcoes: {
        type: Array,
        default: () => []
    },
    titulo: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    // Normalmente dispensável: a obrigatoriedade vem do schema. Serve para forçar o
    // asterisco em regra condicional (`when`), que o schema não expõe estaticamente.
    obrigatorio: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

const campoForm = props.name ? useField(() => props.name) : null;

const selecionado = computed({
    get: () => (campoForm ? campoForm.value.value : props.modelValue) ?? null,
    set: (value) => {
        if (campoForm) campoForm.value.value = value;
        emit('update:modelValue', value);
        emit('change', value);
    }
});

const prefixoId = computed(() => props.id ?? props.name ?? 'radio');

const mensagemErro = computed(() => props.erro ?? campoForm?.errorMessage.value ?? null);

const formulario = inject(FormContextKey, null);

// O asterisco de obrigatório sai da própria regra do schema (ex.: regraRadio() vs
// regraRadio({ obrigatorio: false })), para não ser declarado duas vezes.
const obrigatorioDoSchema = computed(() => {
    if (!props.name) return null;

    try {
        const campo = toValue(formulario?.schema)?.describe?.()?.fields?.[props.name];
        return campo ? campo.optional === false : null;
    } catch {
        return null;
    }
});

// O schema só acrescenta o asterisco: a prop continua vencendo, porque um `when`
// aparece como opcional no describe() e ainda assim é obrigatório em tempo de uso.
const obrigatorio = computed(() => props.obrigatorio || obrigatorioDoSchema.value === true);
</script>

<template>
    <div class="field mb-2" :class="props.classeColuna">
        <SmallTitle v-if="props.titulo" :titulo="props.titulo" :obrigatorio="obrigatorio" />
        <div class="flex flex-column sm:flex-row gap-2">
            <label v-for="opcao in props.opcoes" :key="String(opcao.valor)" :for="`${prefixoId}_${opcao.valor}`"
                class="flex-1 flex align-items-center gap-3 border-1 border-round-lg p-3 transition-colors transition-duration-150"
                :class="[
                    selecionado === opcao.valor ? 'border-primary bg-primary-50' : (mensagemErro ? 'border-red-500' : 'border-300'),
                    props.disabled ? 'opacity-60 cursor-auto' : 'cursor-pointer',
                    { 'hover:surface-50': selecionado !== opcao.valor && !props.disabled }
                ]">
                <span v-if="opcao.icone"
                    class="flex align-items-center justify-content-center w-2rem h-2rem border-round-md flex-shrink-0"
                    :class="selecionado === opcao.valor ? 'bg-primary' : 'surface-100 text-600'">
                    <i :class="[opcao.icone, 'text-sm']"></i>
                </span>
                <span class="flex flex-column gap-1 flex-1">
                    <span class="text-sm font-medium text-900">{{ opcao.rotulo }}</span>
                    <span v-if="opcao.descricao" class="text-xs text-500 line-height-2">{{ opcao.descricao }}</span>
                </span>
                <RadioButton v-model="selecionado" :inputId="`${prefixoId}_${opcao.valor}`" :name="prefixoId"
                    :value="opcao.valor" :disabled="props.disabled" />
            </label>
        </div>
        <SmallError v-if="mensagemErro" :error="mensagemErro" />
    </div>
</template>
